import React, { PropsWithChildren } from 'react';
import { initI18n, Trans } from '@wix/wix-i18n-config';
import { WixDesignSystemProvider } from '@wix/design-system';
import { ApisClientProvider } from '@wix/premium-fed-sdk/@apis-infra-react';
import { PremiumEssentialsSiteLevelProvider } from '@wix/premium-fed-sdk/@premium-essentials-adaptors';
import { PremiumEssentialsProvider } from '@wix/premium-fed-sdk/@premium-essentials';
import { HttpClient } from '@wix/http-client';
import webBiLogger from '@wix/web-bi-logger';
import { TExperiments } from '../types';

const { metaSiteId, locale = 'en' } = window?.__APP_PARAMS__ ?? {};

const i18n = initI18n({ locale });

export type BiLoggerParams = NonNullable<
  Parameters<typeof webBiLogger.factory>[0]
>;

let biLoggerSingleton: ReturnType<
  ReturnType<typeof webBiLogger.factory>['logger']
>;
const getBiLogger = (params?: BiLoggerParams) => {
  if (!biLoggerSingleton) {
    biLoggerSingleton = webBiLogger.factory(params ?? {}).logger();
  }
  return biLoggerSingleton;
};

interface ProviderWrapperProps {
  experiments?: TExperiments;
  referralAdditionalInfo?: string;
  siteName?: string;
  biLoggerParams?: BiLoggerParams;
}

const ProviderWrapper = ({
  children,
  experiments,
  referralAdditionalInfo,
  siteName,
  biLoggerParams,
}: PropsWithChildren<ProviderWrapperProps>) => {
  const props = {
    i18n,
    siteId: metaSiteId,
    biLogger: getBiLogger(biLoggerParams),
    Trans,
    experiments,
    referralAdditionalInfo,
    siteName,
  };

  const httpClient = new HttpClient();

  return metaSiteId ? (
    <PremiumEssentialsSiteLevelProvider {...props}>
      {children}
    </PremiumEssentialsSiteLevelProvider>
  ) : (
    <PremiumEssentialsProvider {...props} httpClient={httpClient}>
      {children}
    </PremiumEssentialsProvider>
  );
};

export const ProvidersWrapper = ({
  children,
  experiments,
  referralAdditionalInfo,
  siteName,
  biLoggerParams,
}: PropsWithChildren<ProviderWrapperProps>) => {
  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <ProviderWrapper
        experiments={experiments}
        referralAdditionalInfo={referralAdditionalInfo}
        siteName={siteName}
        biLoggerParams={biLoggerParams}
      >
        <ApisClientProvider>{children}</ApisClientProvider>
      </ProviderWrapper>
    </WixDesignSystemProvider>
  );
};
